import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  // Core icons
  Home, LogOut, ListTodo,
  // Section icons
  BarChart3, Package, Warehouse, ShoppingCart, Factory, Truck, DoorOpen, Settings,
  // Menu item icons - imported for icon mapping
  Bell, CheckSquare, ClipboardList, GitBranch, ChevronDown, ChevronRight, 
  Building2, Layers, Calendar, FileText, Users, Briefcase, RotateCcw, 
  Database, MapPin, ArrowRightLeft, AlertTriangle, FolderKanban, Wrench, 
  ShieldAlert, Megaphone, Upload, Search, X, Clock, BookOpen, Shield, 
  History, Workflow, Ruler, Calculator, Code, FileCheck, CreditCard, 
  Book, Receipt, FileSpreadsheet, Wallet
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import NotificationPanel from '@/components/NotificationPanel';

// Import from centralized modules config - SINGLE SOURCE OF TRUTH
import { ALL_MODULES, MODULE_CATEGORIES, getModulesByCategory } from '@/config/modules';

// ============================================================================
// ICON MAPPING - Maps string icon names to Lucide React components
// ============================================================================
const ICON_MAP = {
  // Core
  Home, ListTodo,
  // Common
  BarChart3, Package, Warehouse, ShoppingCart, Factory, Truck, DoorOpen, Settings,
  Bell, CheckSquare, ClipboardList, GitBranch, Building2, Layers, Calendar,
  FileText, Users, Briefcase, RotateCcw, Database, MapPin, ArrowRightLeft,
  AlertTriangle, FolderKanban, Wrench, ShieldAlert, Megaphone, Upload,
  Clock, BookOpen, Shield, History, Workflow, Ruler, Calculator, Code,
  FileCheck, CreditCard, Book, Receipt, FileSpreadsheet, Wallet,
};

// Get icon component from string name
const getIcon = (iconName) => ICON_MAP[iconName] || FileText;

// ============================================================================
// CATEGORY CONFIGURATION - Visual styling for each category
// ============================================================================
const CATEGORY_CONFIG = {
  Productivity: { icon: BarChart3, bgColor: 'bg-violet-50/50', iconColor: 'text-violet-600', sectionKey: 'productivity' },
  Masters: { icon: Package, bgColor: 'bg-blue-50/50', iconColor: 'text-blue-600', sectionKey: 'masters' },
  Inventory: { icon: Warehouse, bgColor: 'bg-emerald-50/50', iconColor: 'text-emerald-600', sectionKey: 'inventory' },
  Sales: { icon: ShoppingCart, bgColor: 'bg-amber-50/50', iconColor: 'text-amber-600', sectionKey: 'sales' },
  Production: { icon: Factory, bgColor: 'bg-rose-50/50', iconColor: 'text-rose-600', sectionKey: 'production' },
  Purchase: { icon: Truck, bgColor: 'bg-orange-50/50', iconColor: 'text-orange-600', sectionKey: 'purchase' },
  Stores: { icon: DoorOpen, bgColor: 'bg-cyan-50/50', iconColor: 'text-cyan-600', sectionKey: 'stores' },
  Accounts: { icon: Wallet, bgColor: 'bg-indigo-50/50', iconColor: 'text-indigo-600', sectionKey: 'accounts' },
  Utilities: { icon: Settings, bgColor: 'bg-slate-100/50', iconColor: 'text-slate-600', sectionKey: 'utilities' },
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // ============================================================================
  // SECTION STATE MANAGEMENT
  // ============================================================================
  
  // Determine which section should be open based on current path
  const getSectionFromPath = (path) => {
    // Find the module that matches this path
    // FIX: Exclude root path '/' from startsWith check to prevent Dashboard matching all paths
    const module = ALL_MODULES.find(m => 
      m.path === path || (m.path !== '/' && path.startsWith(m.path))
    );
    if (module && module.category !== 'Core') {
      return CATEGORY_CONFIG[module.category]?.sectionKey || null;
    }
    return null;
  };

  const [openSection, setOpenSection] = useState(() => getSectionFromPath(location.pathname));

  useEffect(() => {
    const newSection = getSectionFromPath(location.pathname);
    if (newSection && newSection !== openSection) {
      setOpenSection(newSection);
    }
  }, [location.pathname]);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Search state for menu filtering
  const [menuSearch, setMenuSearch] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ============================================================================
  // PERMISSION SYSTEM
  // ============================================================================
  // - Owners (is_owner=true): See ALL menu items automatically
  // - Others: See only modules in their allowed_modules (from User Management)
  // ============================================================================
  
  const isModuleAllowed = (moduleKey) => {
    if (user?.is_owner) return true;
    return (user?.allowed_modules || []).includes(moduleKey);
  };

  // Check if user is a restricted employee (non-owner)
  const isRestrictedEmployee = !user?.is_owner;

  // ============================================================================
  // MENU ITEMS - Generated from modules.js
  // ============================================================================
  
  // Core navigation items (Dashboard/My Tasks)
  const navItems = useMemo(() => {
    const coreModules = getModulesByCategory('Core');
    if (isRestrictedEmployee) {
      // Non-owners get "My Dashboard" instead of company dashboard
      return coreModules.map(m => ({
        ...m,
        label: m.key === 'dashboard' ? 'My Dashboard' : m.label,
        icon: getIcon(m.icon)
      }));
    }
    // Owners don't need My Tasks / My Requests
    return coreModules
      .filter(m => m.key !== 'my_tasks' && m.key !== 'my_requests')
      .map(m => ({ ...m, icon: getIcon(m.icon) }));
  }, [isRestrictedEmployee]);

  // Generate menu items for each category from modules.js
  const getMenuItemsForCategory = (category) => {
    return getModulesByCategory(category).map(m => ({
      path: m.path,
      label: m.label,
      icon: getIcon(m.icon),
      moduleKey: m.key
    }));
  };

  // Memoized category items
  const productivityItems = useMemo(() => getMenuItemsForCategory('Productivity'), []);
  const mastersItems = useMemo(() => getMenuItemsForCategory('Masters'), []);
  const inventoryItems = useMemo(() => getMenuItemsForCategory('Inventory'), []);
  const salesItems = useMemo(() => getMenuItemsForCategory('Sales'), []);
  const productionItems = useMemo(() => getMenuItemsForCategory('Production'), []);
  const purchaseItems = useMemo(() => getMenuItemsForCategory('Purchase'), []);
  const storesItems = useMemo(() => getMenuItemsForCategory('Stores'), []);
  const accountsItems = useMemo(() => getMenuItemsForCategory('Accounts'), []);
  const utilitiesItems = useMemo(() => getMenuItemsForCategory('Utilities'), []);

  // ============================================================================
  // FILTERING - Permission + Search
  // ============================================================================

  const filterByPermission = (items) => {
    return items.filter(item => user?.is_owner || isModuleAllowed(item.moduleKey));
  };

  const filterBySearch = (items) => {
    if (!menuSearch.trim()) return items;
    const searchLower = menuSearch.toLowerCase();
    return items.filter(item => 
      item.label.toLowerCase().includes(searchLower) ||
      item.path.toLowerCase().includes(searchLower)
    );
  };

  // Apply permission filter first, then search filter
  // Core nav items (Dashboard, My Tasks) are always shown to all users
  const filteredNavItems = filterBySearch(navItems);
  const searchedProductivityItems = filterBySearch(filterByPermission(productivityItems));
  const searchedMastersItems = filterBySearch(filterByPermission(mastersItems));
  const searchedInventoryItems = filterBySearch(filterByPermission(inventoryItems));
  const searchedSalesItems = filterBySearch(filterByPermission(salesItems));
  const searchedProductionItems = filterBySearch(filterByPermission(productionItems));
  const searchedPurchaseItems = filterBySearch(filterByPermission(purchaseItems));
  const searchedStoresItems = filterBySearch(filterByPermission(storesItems));
  const searchedAccountsItems = filterBySearch(filterByPermission(accountsItems));
  const searchedUtilitiesItems = filterBySearch(filterByPermission(utilitiesItems));

  const isSearchActive = menuSearch.trim().length > 0;

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  // Render a collapsible section
  const renderSection = (sectionKey, categoryName, items) => {
    if (items.length === 0) return null;
    
    const config = CATEGORY_CONFIG[categoryName];
    const SectionIcon = config.icon;
    const isOpen = openSection === sectionKey || isSearchActive;

    return (
      <div className={`pt-2 rounded-md ${isOpen && !isSearchActive ? config.bgColor : ''}`}>
        <button
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center gap-2 px-3 py-1.5 w-full text-muted-foreground hover:text-foreground transition-colors text-sm"
        >
          <SectionIcon className={`w-4 h-4 ${isOpen ? config.iconColor : ''}`} />
          <span className="font-medium flex-1 text-left">{categoryName}</span>
          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5">
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-2 px-3 py-1 ml-5 rounded-md text-xs transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="h-screen bg-background overflow-hidden">
      <div className="flex h-full">
        {/* Sidebar */}
        <aside className="hidden md:flex md:flex-col w-56 flex-shrink-0 border-r border-border bg-card h-screen">
          {/* Logo */}
          <div className="px-3 py-2.5 border-b border-border flex-shrink-0 bg-white overflow-hidden">
            <div className="h-11 overflow-hidden flex items-center">
              <img 
                src="/dreamspan-logo.png" 
                alt="Dreamspan" 
                className="h-10 object-contain"
              />
            </div>
          </div>
          
          {/* Search */}
          <div className="px-2 py-2 border-b border-border flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search menu..."
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                className="pl-7 pr-7 h-8 text-xs"
                uppercase={false}
                data-testid="menu-search-input"
              />
              {menuSearch && (
                <button
                  onClick={() => setMenuSearch('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
            {/* Core items (Dashboard, My Tasks) */}
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all text-sm ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}

            {/* Category sections - rendered dynamically */}
            {renderSection('productivity', 'Productivity', searchedProductivityItems)}
            {renderSection('masters', 'Masters', searchedMastersItems)}
            {renderSection('inventory', 'Inventory', searchedInventoryItems)}
            {renderSection('sales', 'Sales', searchedSalesItems)}
            {renderSection('production', 'Production', searchedProductionItems)}
            {renderSection('purchase', 'Purchase', searchedPurchaseItems)}
            {renderSection('stores', 'Stores', searchedStoresItems)}
            {renderSection('accounts', 'Accounts', searchedAccountsItems)}
            {renderSection('utilities', 'Utilities', searchedUtilitiesItems)}
          </nav>
          
          {/* User profile */}
          <div className="px-2 py-2 border-t border-border flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-2 rounded-md bg-secondary">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{user?.name}</p>
                <Badge variant="outline" className="text-[10px] px-1 py-0">
                  {user?.designation_name || 'User'}
                </Badge>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="w-full mt-1 justify-start text-xs h-7"
            >
              <LogOut className="w-3 h-3 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col h-screen overflow-hidden min-w-0">
          <header className="flex-shrink-0 bg-card border-b border-border">
            <div className="flex items-center justify-between px-6 py-3">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">
                  {ALL_MODULES.find(m => m.path === location.pathname)?.label || 'Dashboard'}
                </h2>
              </div>
              <div className="flex items-center gap-4">
                <NotificationPanel />
              </div>
            </div>
          </header>
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
